import React, { useContext, useEffect, useState } from 'react';
import SvgMenuMessages from '@app/icons/menu/MenuMessages.tsx';
import MenuItem from '@components/menu/MenuItem.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { ApiContext } from '@api/api.tsx';
import {
  CountNotificationOutDTO,
  EntityTypeEnum,
  MessageTypeFilterEnum,
  NotificationPreviewListOutDTO,
  PageResponseNotificationPreviewListOutDTO,
  SupplierNotificationTypeEnum,
} from '@api/ringil-api.v2.tsx';
import useNavigation from '@hooks/useNavigation.tsx';
import InputSelect from '@components/ringil3/Input/InputSelect/InputSelect.tsx';
import { useLocalStorage } from '@utils/utils.tsx';
import { stringDateTimeWithLocaleFromApiStringInTz } from '@utils/dateWithTz.tsx';
import clsx from 'clsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';

interface ComponentProps {
  /** Render as the icon rail. */
  smallMenu?: boolean;
}

const LegacyNotificationMenuItem: React.FC<ComponentProps> = ({ smallMenu }) => {
  const { tr } = useTranslationLgs();
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const [state, setState] = useState<PageResponseNotificationPreviewListOutDTO>();
  const [unreadNotificationCount, setUnreadNotificationCount] = useState<CountNotificationOutDTO>();
  const [notificationMode, setNotificationMode] = useLocalStorage('NOTIFICATION_FILTER', 'ALL');
  const { cuListNotifications, cuReadNotification, cuCountUnreadNotification } = useContext(ApiContext);
  const nav = useNavigation();

  const [minHeight, setMinHeight] = useState<number>(0);
  const [maxVisibleItems, setMaxVisibleItems] = useState<number>(12);

  const ITEMS_COUNT = 12;
  const ITEM_HEIGHT = 64;
  const MIN_VISIBLE_ITEMS = 4;

  useEffect(() => {
    load();
  }, [notificationMode]);

  const compute = () => {
    if (typeof window === 'undefined') return;
    const RESERVED_PX = 3 * ITEM_HEIGHT;
    const available = window.innerHeight - RESERVED_PX;
    const maxItems = Math.max(MIN_VISIBLE_ITEMS, Math.floor(available / ITEM_HEIGHT)) - 2;
    setMaxVisibleItems(maxItems);
  };

  useEffect(() => {
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  const load = () => {
    cuCountUnreadNotification(
      data => {
        setUnreadNotificationCount(data);
        if (data && (data.count > 0 || data.messageCount > 0)) {
          cuListNotifications({
            messageTypeFilter: MessageTypeFilterEnum.UNREAD,
            onlyMessage: notificationMode === 'USER_MESSAGES',
          }, {
            pageSize: ITEMS_COUNT,
            pageNumber: 0,
          }, data => {
            setState(data);
            if (data.size > minHeight) {
              setMinHeight(data.data.length);
            }
            compute();
          });
        }
      },
    );
  };

  const maskAsReadAndRedirect = (n: NotificationPreviewListOutDTO) => {
    cuReadNotification(n.id, n.companyId, n.applicationId, () => {
      if (n.entityType == EntityTypeEnum.SUPPLIER_NOTIFICATION) {
        nav.navigate(nav.urlFunctions.createSupplierNotificationDetail(n.entityId, SupplierNotificationTypeEnum.EFXX));
      } else if (n.entityType == EntityTypeEnum.GROUP_DEMAND) {
        nav.navigate(nav.urlFunctions.createMultiDemanDetail(n.entityId));
      } else {
        const openMessages = n.isMessage === true;
        nav.navigate(nav.urlFunctions.createExpeditionDetailForUser(n.entityId, n.expeditionCoordinate, openMessages, n.selectedCarrierId ?? undefined));
      }
      load();
    });
  };

  const model = [
    {
      code: 'ALL',
      label: tr('Notification.all', 'all'),
    },
    {
      code: 'USER_MESSAGES',
      label: tr('Notification.userMessages', 'User messages'),
    },
  ];

  const content = (
    <div
      className={clsx('flex flex-col w-full')}
      style={{
        minHeight: `${((Math.min(minHeight, maxVisibleItems) ?? ITEMS_COUNT) + 2)  * ITEM_HEIGHT}px`,
        maxHeight: `${(maxVisibleItems + 2) * ITEM_HEIGHT}px`,
      }}
      tabIndex={0}
      onBlur={() => setMinHeight(0)}
      onMouseLeave={() => setMinHeight(0)}
    >
      <div className="w-full flex justify-center items-center my-3 h-16">
        <InputSelect
          model={model}
          selectedItemCode={notificationMode}
          onSelect={v => {
            // state is undefined until notifications load, and stays undefined when the user has none
            // (load() only setState's inside the count>0 branch). Guard so toggling the mode with an
            // empty inbox doesn't deref undefined. (Sentry RINGIL-APP-MN.)
            if (state?.size > minHeight) {
              setMinHeight(state.data.length);
            }
            compute();
            setNotificationMode(v);
          }}
        />
      </div>
      <div className="flex flex-col flex-1 justify-between">
        <div className="flex-col"
             style={{
               minHeight: `${(Math.min(minHeight, maxVisibleItems) ?? ITEMS_COUNT)  * ITEM_HEIGHT}px`,
               maxHeight: `${maxVisibleItems * ITEM_HEIGHT}px`,
               overflowY: 'auto',
             }}
        >
          {state?.data
            .filter(item => notificationMode === 'ALL' || (notificationMode === 'USER_MESSAGES' && item.isMessage))
            .map((n, index) => {
              return (
                <li role="menuitem" key={index}>
                  <button className="p-link topbar-message"
                          onClick={() => maskAsReadAndRedirect(n)}>
                    <div className="h-16 px-4 flex-col justify-center items-start gap-[5px] inline-flex">
                      <div className="">{n.name}</div>
                      <div className="flex flex-row justify-between items-start gap-2.5 text-content-secondary ">
                        <div className="">{n.applicationId}</div>
                        <div className="">{stringDateTimeWithLocaleFromApiStringInTz(n.createAt, loggedUser.defaultTimezoneId)}</div>
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
        </div>
        <div className="flex-col h-16 items-center">
          <MenuItem
            smallMenu={false}
            render={true}
            title={tr(`AppMenuNotification.seeAll`, `Vše`)}
            onClick={() => {
              nav.navigate(nav.urlFunctions.createCustomerNotifications());
            }}
          />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <MenuItem
        title={tr('NotificationMenuItem.messages', 'Messages')}
        icon={<SvgMenuMessages />}
        elementCount={{
          systemMessage: unreadNotificationCount?.count ?? 0,
          userMessage: unreadNotificationCount?.messageCount ?? 0,
        }}
        smallMenu={smallMenu}
        render={true}
        mode={'open-outside-bottom'}
        subMenu={content}
      />
    </>
  );
};

export default LegacyNotificationMenuItem;
